import { useQuery } from '@tanstack/react-query';
import { isEmpty, map } from 'lodash';
import {
  CompanyAggregatedByBOL,
  CompanyTradeDataBOLs,
  PortsAggregatedByBOL,
  TradeDataMarketAnalytics,
} from 'src/model/tradeData';
import { CompanyTradeDataService } from 'src/services/api';
import { TRADE_DATA_CACHE_TIME, TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME } from 'src/utils/constants/config';
import { TypeBuyerSupplier, TypeDataGranularLevel, TypePortData } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { FieldRenameMapper } from 'src/utils/helpers/dataMappers/dataMappers';
import { handleBadRecordName } from 'src/utils/helpers/function/handleBadRecordName';

const QUERY_KEY_LIST_CTD_BILL_OF_LADING = 'QUERY_KEY_LIST_CTD_BILL_OF_LADING';
const QUERY_KEY_LIST_CTD_COMPANY_AGGREGATED_BY_BOL = 'QUERY_KEY_LIST_CTD_COMPANY_AGGREGATED_BY_BOL';
const QUERY_KEY_CHART_CTD_COMPANY_AGGREGATED_BY_BOL = 'QUERY_KEY_CHART_CTD_COMPANY_AGGREGATED_BY_BOL';
const QUERY_KEY_MAP_CTD_COMPANY_AGGREGATED_BY_BOL = 'QUERY_KEY_MAP_CTD_COMPANY_AGGREGATED_BY_BOL';
const QUERY_KEY_LIST_CTD_PORTS_AGGREGATED_BY_BOL = 'QUERY_KEY_LIST_CTD_PORTS_AGGREGATED_BY_BOL';
const QUERY_KEY_CHART_CTD_PORTS_AGGREGATED_BY_BOL = 'QUERY_KEY_CHART_CTD_PORTS_AGGREGATED_BY_BOL';
const QUERY_KEY_MAP_CTD_PORTS_AGGREGATED_BY_BOL = 'QUERY_KEY_MAP_CTD_PORTS_AGGREGATED_BY_BOL';
const QUERY_KEY_TREE_MAP_COMPETITOR = 'QUERY_KEY_TREE_MAP_COMPETITOR';
const QUERY_KEY_LIST_COMPETITOR = 'QUERY_KEY_LIST_COMPETITOR';
const QUERY_KEY_MAP_COMPETITOR = 'QUERY_KEY_MAP_COMPETITOR';
const QUERY_KEY_CTD_MARKET_ANALYTICS_CHART = 'QUERY_KEY_CTD_MARKET_ANALYTICS_CHART';
const QUERY_KEY_CTD_MARKET_ANALYTICS_TABLE = 'QUERY_KEY_CTD_MARKET_ANALYTICS_TABLE';
const QUERY_KEY_TRADE_DATA_TRANSACTIONS_COUNT = 'QUERY_KEY_TRADE_DATA_TRANSACTIONS_COUNT';
const QUERY_KEY_COMPANY_HS_CODE = 'QUERY_KEY_COMPANY_HS_CODE';
const QUERY_KEY_COMPANY_HS_CODE_LIST = 'QUERY_KEY_COMPANY_HS_CODE_LIST';
const QUERY_KEY_COMPANY_TRANSACTIONS_PAYMENT_STATUS = 'QUERY_KEY_COMPANY_TRANSACTIONS_PAYMENT_STATUS';

const shouldExistAtLeastKeywordOrCompany: (query: any) => boolean = (query: any) => {
  return !isEmpty(query?.company_id) || !isEmpty(query?.keyword);
};

const shouldMinimumKeywordLength: (query: any) => boolean = (query: any) => {
  return query?.keyword && query?.keyword.length >= 3;
};

const QUERY_KEY_TRADE_DATA_COMPANY_DETAIL = 'QUERY_KEY_TRADE_DATA_COMPANY_DETAIL';

export const useQueryListCTDBOLs = (id: string, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_CTD_BILL_OF_LADING, id, query],
    queryFn: () => CompanyTradeDataService.getListCTDBOLs(id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyTradeDataBOLs(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const mappedData = getArrayDefault(data?.data).map((item) => ({
    ...item,
    bol: handleBadRecordName(item.bol),
  }));

  return {
    data: {
      ...data,
      data: mappedData,
    },
    isFetching,
    refetch,
  };
};

export const useQueryListCTDCompanyAggregatedByBOL = (id: string, type: TypeBuyerSupplier, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_CTD_COMPANY_AGGREGATED_BY_BOL, id, type, query],
    queryFn: () => CompanyTradeDataService.getListCTDCompanyAggregatedByBOL(id, type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryChartCTDCompanyAggregatedByBOL = (id: string, type: TypeBuyerSupplier, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_CHART_CTD_COMPANY_AGGREGATED_BY_BOL, id, type, query],
    queryFn: () => CompanyTradeDataService.getChartCTDCompanyAggregatedByBOL(id, type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryMapCTDCompanyAggregatedByBOL = (id: string, type: TypeBuyerSupplier, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_MAP_CTD_COMPANY_AGGREGATED_BY_BOL, id, type, query],
    queryFn: () => CompanyTradeDataService.getMapCTDCompanyAggregatedByBOL(id, type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryListCTDPortsAggregatedByBOL = (id: string, type: TypePortData, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_CTD_PORTS_AGGREGATED_BY_BOL, id, type, query],
    queryFn: () => CompanyTradeDataService.getListCTDPortsAggregatedByBOL(id, type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PortsAggregatedByBOL(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const prefixFieldName = type === TypePortData.pod ? 'import_port' : 'export_port';
  const dataMapping: ITradeDataByProportionalPort[] = data?.data?.map((item) =>
    FieldRenameMapper.rename<any, ITradeDataByProportionalPort>(item, {
      id: `${prefixFieldName}_id`,
      port_id: `${prefixFieldName}_id`,
      total_buyers: 'buyers',
      total_suppliers: 'suppliers',
      // port_code: `${prefixFieldName}_code`,
      // port_name: `${prefixFieldName}_name`,
      // country_code: `${prefixFieldName}_country_code`,
      // longitude: `${prefixFieldName}_longitude`,
      // latitude: `${prefixFieldName}_latitude`,
      // port_locode: `${prefixFieldName}_locode`,
      teu: 'quantity',
      shipments: 'bol',
      quantity: 'quantity',
      bol: 'bol',
    }),
  );
  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryMapCTDPortsAggregatedByBOL = (id: string, type: TypePortData, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_MAP_CTD_PORTS_AGGREGATED_BY_BOL, id, type, query],
    queryFn: () => CompanyTradeDataService.getMapCTDPortsAggregatedByBOL(id, type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PortsAggregatedByBOL(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const prefixFieldName = type === TypePortData.pod ? 'import_port' : 'export_port';

  const dataMapping: ITradeDataByProportionalPort[] = data?.data?.map((item) =>
    FieldRenameMapper.rename<any, ITradeDataByProportionalPort>(item, {
      id: `${prefixFieldName}_id`,
      port_id: `${prefixFieldName}_id`,
      total_buyers: 'buyers',
      total_suppliers: 'suppliers',
      // port_code: `${prefixFieldName}_code`,
      // port_name: `${prefixFieldName}_name`,
      // country_code: `${prefixFieldName}_country_code`,
      // longitude: `${prefixFieldName}_longitude`,
      // latitude: `${prefixFieldName}_latitude`,
      longitude_fallback: `${prefixFieldName}_country_longitude`,
      latitude_fallback: `${prefixFieldName}_country_latitude`,
      teu: 'quantity',
      shipments: 'bol',
      quantity: 'quantity',
      bol: 'bol',
    }),
  );
  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryChartCTDPortsAggregatedByBOL = (id: string, type: TypePortData, query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_CHART_CTD_PORTS_AGGREGATED_BY_BOL, id, type, query],
    queryFn: () => CompanyTradeDataService.getChartCTDPortsAggregatedByBOL(id, type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PortsAggregatedByBOL(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const prefixFieldName = type === TypePortData.pod ? 'import_port' : 'export_port';

  const dataMapping: ITradeDataByProportionalPort[] = data?.data?.map((item) =>
    FieldRenameMapper.rename<any, ITradeDataByProportionalPort>(item, {
      id: `${prefixFieldName}_id`,
      port_id: `${prefixFieldName}_id`,
      total_buyers: 'buyers',
      total_suppliers: 'suppliers',
      // port_code: `${prefixFieldName}_code`,
      // port_name: `${prefixFieldName}_name`,
      // country_code: `${prefixFieldName}_country_code`,
      // longitude: `${prefixFieldName}_longitude`,
      // latitude: `${prefixFieldName}_latitude`,
      teu: 'quantity',
      quantity: 'quantity',
      shipments: 'bol',
      bol: 'bol',
    }),
  );
  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryTreemapChartCompetitor = ({
  id = '',
  query,
  enabled,
}: {
  id?: string;
  query: Record<string, ExpectedAny>;
  enabled: boolean;
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_TREE_MAP_COMPETITOR, id, query],
    queryFn: () => CompanyTradeDataService.getCompetitorTreemap(id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    enabled: enabled,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
};
export const useQueryListCompetitor = ({
  id = '',
  query,
  enabled,
}: {
  id?: string;
  query: Record<string, ExpectedAny>;
  enabled: boolean;
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_LIST_COMPETITOR, id, query],
    queryFn: () => CompanyTradeDataService.getCompetitorList(id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    enabled: enabled,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
};
export const useQueryMapCompetitor = ({
  id = '',
  query,
  enabled,
}: {
  id?: string;
  query: Record<string, ExpectedAny>;
  enabled: boolean;
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_MAP_COMPETITOR, id, query],
    queryFn: () => CompanyTradeDataService.getCompetitorWorldMap(id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    enabled: enabled,
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryGetCTDMarketAnalyticsChart = (id: string, query: any, granularLevel: TypeDataGranularLevel) => {
  const { data, isFetching, refetch, status } = useQuery({
    queryKey: [QUERY_KEY_CTD_MARKET_ANALYTICS_CHART, query, granularLevel],
    queryFn: () => CompanyTradeDataService.getCTDMarketAnalyticsChart(id, query, granularLevel),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalytics(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!query,
  });

  return {
    data: {
      ...data,
      data: data?.data?.map((item) =>
        FieldRenameMapper.rename<any, IMarketAnalytics>(item, {
          date: granularLevel === TypeDataGranularLevel.monthly ? 'month' : 'year',
          number_of_bol: 'bol',
        }),
      ),
    },
    isFetching,
    refetch,
    status,
  };
};

export const useQueryGetCTDMarketAnalyticsList = (id: string, query: any, granularLevel: TypeDataGranularLevel) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_CTD_MARKET_ANALYTICS_TABLE, query, granularLevel, id],
    queryFn: () => CompanyTradeDataService.getCTDMarketAnalyticsList(id, query, granularLevel),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalytics(),
    placeholderData: (previousData) => previousData,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!query,
  });

  const dataMapping = map(data?.data, (item) =>
    FieldRenameMapper.rename<any, IMarketAnalytics>(item, {
      date: granularLevel === TypeDataGranularLevel.monthly ? 'month' : 'year',
      number_of_bol: 'bol',
      value: 'value_usd',
      value_usd: 'value_usd',
    }),
  );

  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryCTDTransactionsCount = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_TRANSACTIONS_COUNT, query],
    queryFn: () => CompanyTradeDataService.getTransactionsCount(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    enabled: shouldExistAtLeastKeywordOrCompany(query) && shouldMinimumKeywordLength(query),
  });

  return {
    data,
    isFetching,
    refetch,
  };
};
export const useQueryCompanyTradeDataHSCode = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_HS_CODE, params],
    queryFn: () => CompanyTradeDataService.getCompanyTradeDataHSCodeChart(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    enabled: !!params.company_id,
  });

  return {
    data,
    isFetching,
  };
};
export const useQueryCompanyTradeDataHSCodeList = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_HS_CODE_LIST, params],
    queryFn: () => CompanyTradeDataService.getCompanyTradeDataHSCodeList(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    enabled: !!params.company_id,
  });

  return {
    data,
    isFetching,
  };
};

export const useQueryTradeDataCompanyDetail = (id = '') => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_COMPANY_DETAIL, id],
    queryFn: () => CompanyTradeDataService.getCompanyDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!id,
  });

  return {
    data,
    isFetching,
  };
};

export const useQueryCTDTransactionsPaymentStatus = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_TRANSACTIONS_PAYMENT_STATUS, query],
    queryFn: () => CompanyTradeDataService.getTransactionsPaymentStatus(query),
    placeholderData: (previousData) => previousData,
    enabled: shouldExistAtLeastKeywordOrCompany(query) && shouldMinimumKeywordLength(query),
  });

  return {
    data,
    isFetching,
    refetch,
  };
};
